/**
 * @file CompanyAdminLayout.tsx
 * @brief Layout shell for the Company Admin space (top mega menu + footer).
 *
 * @author DES
 * @date 26-05-2026
 */

import { useMemo } from "react";
import { AdminShellLayout } from "./components/AdminShellLayout";
import { buildCompanyNavConfig } from "./components/companyNavConfig";
import { useAuth } from "@/shared/auth/auth-context";
import { useCompanyFeatures } from "@/features/company-features/api";
import { useCompanyHasSessions } from "@/features/company-sessions/api";
import { env } from "@/shared/config/env";
import { companyIdOf, webInterpretAllowed } from "@/features/interpret/pilot";

export function CompanyAdminLayout() {
  const { user } = useAuth();
  const subtitle = user?.company_name || "Company Admin";
  // Company product toggles gate product-action nav (e.g. interpretation
  // scheduling) — hidden when the company has that product off. (15-07-2026)
  const { data: featuresPayload } = useCompanyFeatures();
  // The Sessions link only shows once the company has at least one
  // interpretation session (undefined while loading → link stays visible).
  const { data: hasSessions } = useCompanyHasSessions();
  const webInterpret = webInterpretAllowed(companyIdOf(user), env.webInterpretCompanies);

  const nav = useMemo(
    () =>
      buildCompanyNavConfig(
        subtitle,
        featuresPayload?.featureAccess,
        hasSessions,
        featuresPayload?.platformLocks,
        webInterpret,
      ),
    [subtitle, featuresPayload, hasSessions, webInterpret],
  );

  return <AdminShellLayout nav={nav} />;
}
